import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeftLong } from "@fortawesome/pro-regular-svg-icons";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth } from "../../providers/AuthContext";
import { getAddOnService, updateAddOnService } from "../../api/AddOnService";
import toast from "react-hot-toast";
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingTablePage from "../LoadingTablePage";
import Breadcrumbs from "../../components/Breadcrumbs";
import { getBillingGroupsByAgreement } from "../../api/BillingGroups"; // Import the API function
import AddOnServiceForm from "../../components/addOnServices/AddOnServiceForm";

const EditAddOnServicePage = () => {
  const { getAccessToken } = useAuth();
  const { addOnServicePublicId, agreementPublicId } = useParams();
  const navigate = useNavigate();

  const [addOnService, setAddOnService] = useState(null);
  const [loadingAddOnService, setLoadingAddOnService] = useState(true);
  const [hasChanges, setHasChanges] = useState(false);
  const [confirmState, setConfirmState] = useState({ isOpen: false, id: "" });
  const [billingGroups, setBillingGroups] = useState([]);
  const [fetchTimestamp, setFetchTimestamp] = useState(
    new Date().toISOString()
  );

  useEffect(() => {
    handleGetAddOnService();
    handleGetBillingGroups();
  }, [addOnServicePublicId]);

  const handleGetAddOnService = async () => {
    try {
      const accessToken = await getAccessToken();
      const data = await getAddOnService(
        addOnServicePublicId,
        accessToken
      );

      setAddOnService(data);
      setFetchTimestamp(); // Spara tidsstämpeln
    } catch (error) {
      console.error("Failed to fetch addOnService:", error);
    } finally {
      setLoadingAddOnService(false);
    }
  };

  const handleGetBillingGroups = async () => {
    try {
      const accessToken = await getAccessToken();
      const bgroups = await getBillingGroupsByAgreement(
        agreementPublicId,
        accessToken
      );
      setBillingGroups(bgroups);
    } catch (error) {
      console.error("Failed to fetch billing groups:", error);
    }
  };

  const handleBackClick = () => {
    if (hasChanges) {
      setConfirmState((s) => ({
        ...s,
        isOpen: true,
        title: "Discard changes?",
        confirm: () => {
          navigate(-1);
        },
      }));
    } else {
      navigate(-1);
    }
  };

  const handleBillingGroupChange = (selectedName) => {
    const selectedGroup = billingGroups.find(
      (group) => group.name === selectedName
    );
    if (!selectedGroup) return;

    setAddOnService((prevAddOnService) => ({
      ...prevAddOnService,
      billingGroupName: selectedGroup.name,
      billingGroupPublicId: selectedGroup.billingGroupPublicId,
      billingGroupStatus: selectedGroup.status || "Active", // Ensure status is updated
    }));

    setHasChanges(true);
  };

  const handleUpdateAddOnService = async () => {
    try {
      const accessToken = await getAccessToken();

      if (!accessToken) {
        toast.error("Authorization failed. Please log in again.");
        return;
      }

      const addOnServiceData = {
        addOnServicePublicId: addOnService.addOnServicePublicId,
        billingGroupPublicId: addOnService.billingGroupPublicId,
        name: addOnService.name,
        comment: addOnService.comment,
        price: addOnService.price,
        status: addOnService.status,
        updatedDateTimeUTC: new Date().toISOString(),
      };

      await updateAddOnService(addOnServiceData, accessToken);

      toast.success("addOnService updated successfully!", {
        iconTheme: {
          primary: "#4CAF50",
          secondary: "#DFF2BF",
        },
      });

      console.log("addOnServiceData:", addOnServiceData);

      window.dispatchEvent(new Event("addonservice:updated"));

      setHasChanges(false);
    } catch (error) {
      toast.error("Failed to save addOnService");
    }
    navigate(-1);
    };

  const saveAndExit = () => {
    handleUpdateAddOnService();
  };

  console.log("addOnService:", addOnService);

  return (
    <>
      <HeaderBar />
      <div className="flex flex-row w-full">
        <Sidebar />
        <main className="page">
          <div className="absolute w-full pointer-events-none">
            <LoadingTablePage
              title={"Add on service"}
              loading={loadingAddOnService}
            />
          </div>
          <div
            className={`${loadingAddOnService ? "loading" : ""} page-content`}
          >
            <Breadcrumbs />

            <div className="flex flex-row items-center pb-4">
              <button onClick={handleBackClick} className="text-black flex">
                <FontAwesomeIcon icon={faArrowLeftLong} className="w-4 pr-4" />
              </button>
              <h2 className="border-l border-mute-200 pl-4">
                {`Edit add-on service: ${addOnService?.name || "Unknown"}`}
              </h2>
              <div className="flex gap-4 ml-auto">
                <button
                  className="btn btn-outline-primary"
                  onClick={handleBackClick}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-primary"
                  disabled={!hasChanges}
                  onClick={saveAndExit}
                >
                  Save
                </button>
              </div>
            </div>
            <div className="flex flex-col px-4 py-8 shadow-lg border rounded-md w-full gap-4">
              <AddOnServiceForm
                addOnService={addOnService}
                setAddOnService={setAddOnService}
                setHasChanges={setHasChanges}
                billingGroups={billingGroups}
                onBillingGroupChange={handleBillingGroupChange}
                disabledFields={[                                  
                  "billingGroupStatus",
                  "addOnServicePublicId",
                  "billingGroupPublicId",
                  "agreementPublicId",
                  "createdBy",
                  "createdDate",
                  "lastUpdatedBy",
                  "lastUpdatedDate",                  
                  "updatedBy",
                  "createdDateTimeUTC",                  
                  "updatedDateTimeUTC",
                ]}
              />

              {/*               <EditForm
                data={product || {}}
                setData={setProduct}
                setHasChanges={setHasChanges}
                disabled={false}
                fields={formFields}
                dropdownOptions={{
                  billingGroupName: billingGroups.map((g) => ({
                    name: g.name,
                    billingGroupPublicId: g.billingGroupPublicId,
                  })),
                }}
              /> */}
            </div>
          </div>
        </main>
        <ConfirmationModal
          isOpen={confirmState.isOpen}
          title={confirmState.title ? confirmState.title : "Discard changes?"}
          onClose={() => {
            setConfirmState((s) => ({ ...s, isOpen: false }));
          }}
          onConfirm={() => {
            setConfirmState((s) => ({ ...s, isOpen: false }));
            confirmState.confirm();
          }}
        />
      </div>
    </>
  );
};

export default EditAddOnServicePage;
